import intersectSquareIcon from "assets/svg/IntersectSquare.svg"
import prohibitInsetIcon from "assets/svg/ProhibitInset.svg"
import type {
  ColumnFieldDef,
  FailedCategoryConfig,
  RequiredColumn,
} from "./types"

const IntersectSquareIcon = ({ className }: { className?: string }) => (
  <img
    src={intersectSquareIcon}
    alt=""
    className={className}
  />
)

const ProhibitInsetIcon = ({ className }: { className?: string }) => (
  <img
    src={prohibitInsetIcon}
    alt=""
    className={className}
  />
)

// Common synonyms for column auto-validation (used to detect obvious mis-maps).
export const COLUMN_SYNONYMS: Record<string, string[]> = {
  date: ["date", "day", "work date", "attendance date", "shift date"],
  employeeId: [
    "employee id",
    "employee",
    "emp id",
    "empid",
    "id",
    "worker no",
    "worker number",
    "worker",
    "staff id",
  ],
  clockIn: ["clock in", "clockin", "clock-in", "in time", "time in", "start"],
  clockOut: [
    "clock out",
    "clockout",
    "clock-out",
    "out time",
    "time out",
    "end",
  ],
  shiftPeriodId: [
    "shift period id",
    "shift period",
    "period id",
    "period",
    "shift id",
  ],
  clockInLatitude: [
    "clock in latitude",
    "clock-in latitude",
    "in latitude",
    "in lat",
  ],
  clockInLongitude: [
    "clock in longitude",
    "clock-in longitude",
    "in longitude",
    "in lng",
    "in lon",
  ],
  clockOutLatitude: [
    "clock out latitude",
    "clock-out latitude",
    "out latitude",
    "out lat",
  ],
  clockOutLongitude: [
    "clock out longitude",
    "clock-out longitude",
    "out longitude",
    "out lng",
    "out lon",
  ],
}

export const SETUP_STEPS = ["Upload sheet", "Map columns", "Review and confirm"]

export const REQUIRED_FIELDS: ColumnFieldDef[] = [
  { stateKey: "date", title: "Date" },
  { stateKey: "employeeId", title: "Employee ID" },
  { stateKey: "clockIn", title: "Clock-in time" },
  { stateKey: "clockOut", title: "Clock-out time" },
  { stateKey: "shiftPeriodId", title: "Shift period ID" },
]

export const OPTIONAL_FIELDS: ColumnFieldDef[] = [
  { stateKey: "clockInLatitude", title: "Clock-in latitude" },
  { stateKey: "clockInLongitude", title: "Clock-in longitude" },
  { stateKey: "clockOutLatitude", title: "Clock-out latitude" },
  { stateKey: "clockOutLongitude", title: "Clock-out longitude" },
]

const ENTRY_RED = "text-red-600"
const DUPLICATE_BLUE = "text-[#62748E]"

const employeeUnit = (n: number) => (n === 1 ? "employee" : "employees")
const rowUnit = (n: number) => (n === 1 ? "row" : "rows")

export const FAILED_CATEGORY_CONFIG: Record<string, FailedCategoryConfig> = {
  // Duplicate (its own section, special CTA).
  duplicate_entry: {
    title: "Previously imported conflicts",
    description: () =>
      "This CSV has conflicting records for some dates that have been previously imported.",
    linkLabel: "Resolve conflicts",
    Icon: IntersectSquareIcon,
    group: "duplicate",
    iconColor: DUPLICATE_BLUE,
  },

  // Entry-level errors.
  employee_not_found: {
    title: "Employee not found",
    description: (n) =>
      `${n} ${employeeUnit(
        n
      )} in your sheet could not be matched to any employee in this workplace. Correct these in your file and re-upload.`,
    linkLabel: "View affected rows",
    Icon: ProhibitInsetIcon,
    group: "entry",
    iconColor: ENTRY_RED,
  },
  shift_period_not_found: {
    title: "Shift period not found",
    description: (n) =>
      `${n} ${rowUnit(
        n
      )} reference a Shift Period ID that doesn't exist in the system. Download the reference sheet to get the correct IDs.`,
    linkLabel: "View affected rows",
    Icon: ProhibitInsetIcon,
    group: "entry",
    iconColor: ENTRY_RED,
  },
  no_shift_plan: {
    title: "No shift plan",
    description: (n) =>
      `${n} ${employeeUnit(
        n
      )} in your sheet are not assigned to a shift plan in the system. Correct these in your file and re-upload.`,
    linkLabel: "View affected rows",
    Icon: ProhibitInsetIcon,
    group: "entry",
    iconColor: ENTRY_RED,
  },
  invalid_shift_assignment: {
    title: "No valid assignment",
    description: (n) =>
      `${n} ${rowUnit(
        n
      )} are for employees who aren't assigned to the specified shift period on the given date. Correct these in your file and re-upload.`,
    linkLabel: "View affected rows",
    Icon: ProhibitInsetIcon,
    group: "entry",
    iconColor: ENTRY_RED,
  },
  invalid_shift_plan: {
    title: "Invalid shift plan",
    description: (n) =>
      `${n} ${rowUnit(
        n
      )} reference a shift plan that isn't valid for the employee on the given date. Correct these in your file and re-upload.`,
    linkLabel: "View affected rows",
    Icon: ProhibitInsetIcon,
    group: "entry",
    iconColor: ENTRY_RED,
  },
  wrong_workplace: {
    title: "Wrong workplace",
    description: (n) =>
      `${n} ${rowUnit(
        n
      )} reference employees who belong to a different workplace. Correct these in your file and re-upload.`,
    linkLabel: "View affected rows",
    Icon: ProhibitInsetIcon,
    group: "entry",
    iconColor: ENTRY_RED,
  },

  // Field-level errors.
  missing_required_fields: {
    title: "Missing required field",
    description: (n) =>
      `${n} ${rowUnit(
        n
      )} are missing one or more required values (Date, Employee ID, Clock-in Time, Clock-out Time, or Shift Period ID). Correct these in your file and re-upload.`,
    linkLabel: "View affected rows",
    Icon: ProhibitInsetIcon,
    group: "field",
    iconColor: ENTRY_RED,
  },
  invalid_date_time: {
    title: "Invalid date or time",
    description: (n) =>
      `${n} ${rowUnit(
        n
      )} have a date or time value that couldn't be read. Correct these in your file and re-upload.`,
    linkLabel: "View affected rows",
    Icon: ProhibitInsetIcon,
    group: "field",
    iconColor: ENTRY_RED,
  },
  clockout_time_before_clockin_time: {
    title: "Clock-out before clock-in",
    description: (n) =>
      `${n} ${rowUnit(
        n
      )} have a clock-out time that is earlier than the clock-in time. Correct these in your file and re-upload.`,
    linkLabel: "View affected rows",
    Icon: ProhibitInsetIcon,
    group: "field",
    iconColor: ENTRY_RED,
  },
  future_date: {
    title: "Future date",
    description: (n) =>
      `${n} ${rowUnit(
        n
      )} have a date in the future. Attendance records can only be uploaded for dates that have already passed. Correct these in your file and re-upload.`,
    linkLabel: "View affected rows",
    Icon: ProhibitInsetIcon,
    group: "field",
    iconColor: ENTRY_RED,
  },

  // File-content errors.
  overlapping_entries: {
    title: "Overlapping entries",
    description: (n) =>
      `${n} ${rowUnit(
        n
      )} have clock-in/clock-out times that overlap with another entry for the same employee on the same date — that is, one shift's clock-out is after another shift's clock-in. Correct these in your file and re-upload.`,
    linkLabel: "View affected rows",
    Icon: ProhibitInsetIcon,
    group: "content",
    iconColor: ENTRY_RED,
  },

  // Generic fallback used when the backend reports a failure type the
  // frontend doesn't recognize. Also acts as the default config for any
  // unmapped type at runtime, so new backend categories surface as a
  // visible error card instead of silently disappearing.
  unknown_error: {
    title: "Unknown error",
    description: (n) =>
      `${n} ${rowUnit(
        n
      )} couldn't be imported due to an unexpected error. Review the affected rows below.`,
    linkLabel: "View affected rows",
    Icon: ProhibitInsetIcon,
    group: "content",
    iconColor: ENTRY_RED,
  },
}

export const FAILED_GROUP_ORDER = [
  "duplicate",
  "entry",
  "field",
  "content",
] as const

export const DUPLICATE_TABLE_COLS: number[] = [
  154, 154, 154, 154, 154, 130, 200,
]

export const TIMESHEET_DRAWER_WIDTH = 1150

export const DEFAULT_REQUIRED_COLUMNS: RequiredColumn[] = [
  { title: "Date" },
  { title: "Employee ID" },
  { title: "Clock-in Time" },
  { title: "Clock-out Time" },
  { title: "Shift period ID" },
]

export const DEFAULT_OPTIONAL_COLUMNS: RequiredColumn[] = [
  { title: "Clock-in latitude" },
  { title: "Clock-in longitude" },
  { title: "Clock-out latitude" },
  { title: "Clock-out longitude" },
]
